/**
 * DocumentReviewPanel — Staff quality verdict for a document.
 *
 * Review status certifies whether a document is usable as submitted.
 * Classification says what it is. Requirement Match says what it satisfies.
 * Document Review says: is it legible, complete, correct for the stated period?
 *
 * Actions are contextual by current review state — never show all buttons at once:
 *   unreviewed / review_required → Start Review
 *   in_review / needs_clarification → Accept · Needs Replacement · Supersede · Disregard
 *   terminal (accepted / not_needed / …) → status label + Reopen
 *
 * Duplicate removed as a separate action. Disregard (mark_not_needed) subsumes it.
 * Document Request Matches and RequirementMatchCard moved to dedicated Requirement Match section.
 */
import { useState } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const chip = (s) => (s || "").replace(/_/g, " ");

// Actions available when document is in_review or needs_clarification
const IN_REVIEW_ACTIONS = [
  { id: "accept",      action: "accept",                 label: "Accept",            icon: "Check",     tone: "emerald", help: "Document is usable and satisfies its intended purpose" },
  { id: "replacement", action: "mark_needs_replacement", label: "Needs Replacement", icon: "RefreshCw", tone: "amber",   help: "Document is incomplete, illegible, incorrect or for wrong period" },
  { id: "superseded",  action: "mark_superseded",        label: "Supersede",         icon: "Layers",    tone: "slate",   help: "A newer version replaces this document" },
  { id: "disregard",   action: "mark_not_needed",        label: "Disregard",         icon: "EyeOff",    tone: "slate",   help: "Remove from active workspace — preserved and recoverable" },
];

const TERMINAL_REVIEW = ["accepted", "needs_replacement", "duplicate", "superseded", "rejected",
                          "not_applicable", "not_needed"];

const REVIEW_TONE = {
  unreviewed:          "bg-slate-100 text-slate-500",
  in_review:           "bg-amber-100 text-amber-700",
  accepted:            "bg-emerald-100 text-emerald-700",
  needs_replacement:   "bg-amber-100 text-amber-700",
  duplicate:           "bg-orange-100 text-orange-700",
  superseded:          "bg-slate-100 text-slate-400",
  rejected:            "bg-rose-100 text-rose-600",
  not_applicable:      "bg-slate-100 text-slate-400",
  needs_clarification: "bg-indigo-100 text-indigo-700",
  not_needed:          "bg-slate-100 text-slate-400",
};

const TONE_BTN = {
  emerald: "border-emerald-300 text-emerald-700 hover:bg-emerald-50",
  amber:   "border-amber-300 text-amber-700 hover:bg-amber-50",
  slate:   "border-slate-200 text-slate-600 hover:bg-slate-50",
  blue:    "border-blue-300 text-blue-700 hover:bg-blue-50",
};

export default function DocumentReviewPanel({ docId, reviewStatus, onChange, onDisregard }) {
  const [relDocId, setRelDocId]         = useState("");
  const [showRelInput, setShowRelInput] = useState(false); // Supersede related-doc prompt

  const doAction = async (action, opts = {}) => {
    const { reason, relatedDocumentId } = opts;
    if (action === "mark_needs_replacement") {
      const r = window.prompt("Reason (required):"); if (!r) return;
      return doAction(action, { reason: r });
    }
    try {
      const payload = {
        action,
        ...(reason ? { reason } : {}),
        ...(relatedDocumentId ? { related_document_id: relatedDocumentId } : {}),
      };
      const { data } = await api.post(`/documents-intel/${docId}/review/action`, payload);
      toast.success(`Review: ${chip(data.review_status)}`);
      onChange?.();
      if (action === "mark_not_needed") onDisregard?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const confirmSupersede = () => {
    doAction("mark_superseded", { related_document_id: relDocId || undefined });
    setShowRelInput(false); setRelDocId("");
  };

  const isUnreviewed = !reviewStatus || ["unreviewed", "review_required"].includes(reviewStatus);
  const isInReview   = ["in_review", "needs_clarification"].includes(reviewStatus);
  const isTerminal   = TERMINAL_REVIEW.includes(reviewStatus);

  return (
    <div className="space-y-2.5" data-testid="document-review-panel">
      {/* Status badge */}
      <span
        className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium capitalize ${REVIEW_TONE[reviewStatus] || "bg-slate-100 text-slate-500"}`}
        data-testid="review-status-badge"
      >
        {chip(reviewStatus) || "Unreviewed"}
      </span>

      {/* Supersede: related document input */}
      {showRelInput && (
        <div className="p-2 border border-slate-200 rounded-lg bg-slate-50 space-y-1.5">
          <div className="text-[11px] text-slate-500">Related document ID <span className="text-slate-400">(optional)</span></div>
          <input
            value={relDocId}
            onChange={(e) => setRelDocId(e.target.value)}
            placeholder="e.g. abc123"
            className="w-full text-xs border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-400"
            data-testid="review-related-doc-input"
          />
          <div className="flex gap-1.5">
            <button onClick={confirmSupersede} className="text-[11px] bg-slate-800 text-white rounded px-2.5 py-1" data-testid="review-related-confirm">Confirm</button>
            <button onClick={() => { setShowRelInput(false); setRelDocId(""); }} className="text-[11px] border border-slate-200 rounded px-2.5 py-1">Cancel</button>
          </div>
        </div>
      )}

      {/* Contextual action buttons */}
      <div className="flex flex-wrap gap-1.5">
        {/* Unreviewed / Review Required → Start Review only */}
        {isUnreviewed && (
          <button
            onClick={() => doAction("start_review")}
            data-testid="review-action-start_review"
            className={`inline-flex items-center gap-1 text-[11px] border rounded-md px-2 py-1 ${TONE_BTN.blue}`}
          >
            <Icons.Play className="h-3 w-3" />Start Review
          </button>
        )}

        {/* In Review → Accept, Needs Replacement, Supersede, Disregard */}
        {isInReview && IN_REVIEW_ACTIONS.map(({ id, action, label, icon, tone, help }) => {
          const Ic = Icons[icon] || Icons.Circle;
          return (
            <button
              key={id}
              onClick={() => action === "mark_superseded" ? setShowRelInput(true) : doAction(action)}
              data-testid={`review-action-${id}`}
              title={help}
              className={`inline-flex items-center gap-1 text-[11px] border rounded-md px-2 py-1 ${TONE_BTN[tone] || TONE_BTN.slate}`}
            >
              <Ic className="h-3 w-3" />{label}
            </button>
          );
        })}

        {/* Terminal → Reopen */}
        {isTerminal && (
          <button
            onClick={() => doAction("reopen")}
            data-testid="review-action-reopen"
            className={`inline-flex items-center gap-1 text-[11px] border rounded-md px-2 py-1 ${TONE_BTN.slate}`}
          >
            <Icons.RotateCcw className="h-3 w-3" />Reopen
          </button>
        )}
      </div>
    </div>
  );
}
